<template>
  <div
    data-cy="sms-card"
    class="group"
    :class="isCollapsed && index ? 'absolute' : 'relative'"
    :style="cardStyles"
  >
    <div
      v-if="isTopLevel && isCollapsed"
      class="absolute -top-2 -left-2 z-[101] flex hidden h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-dim-grey group-hover:flex"
    >
      <icon-base
        :icon="IconClose"
        width="12"
        height="12"
        view-box="0 0 18 18"
        @click.stop="emit('close')"
      />
    </div>
    <div
      class="group/header relative z-100 inline-flex w-80 flex-col items-start justify-start overflow-hidden rounded-lg text-white shadow-dialer"
      :style="bgStyle"
    >
      <div
        data-cy="collapse-toggle"
        class="inline-flex cursor-pointer items-start justify-start gap-3 self-stretch border-b border-white/10 p-4"
        @click="
          () => (isTopLevel && isCollapsed ? emit('collapsed:toggle') : null)
        "
      >
        <div class="flex-none">
          <img src="/images/twilio.svg" class="h-10 w-10" loading="lazy" />
        </div>
        <div class="inline-flex flex-1 flex-col items-start justify-center">
          <div
            data-cy="sender-name"
            class="justify-center self-stretch text-sm leading-tight font-semibold"
          >
            {{ sms?.data?.sender_full_name ?? sms?.data?.from ?? "-" }}
          </div>
          <div
            data-cy="sms-message"
            class="line-clamp-2 justify-center self-stretch text-xs leading-[18px] font-medium"
          >
            {{ sms?.data?.message }}
          </div>
        </div>
        <div
          class="relative hidden h-6 w-6 items-center justify-center rounded bg-white/10 group-hover/header:flex"
        >
          <icon-base
            data-cy="open-sms-conversation"
            :icon="IconLineArrowRight"
            width="16"
            height="16"
            view-box="0 1 12 12"
            @click.stop="emit('open', sms.sms_conversation_id, sms.sms_log_id)"
          />
        </div>
      </div>
      <div
        v-if="infoRows.length"
        class="flex flex-col items-start justify-start gap-3 self-stretch p-4"
      >
        <div
          v-for="row in infoRows"
          :key="row.name"
          class="sms-session-info-row group"
        >
          <icon-base
            width="12"
            height="12"
            :view-box="row.iconOptions.viewBox"
            :icon="row.iconOptions.icon"
            class="icon"
          />
          <span v-if="row.label" class="shrink-0 text-label">
            {{ row.label }}:
          </span>
          <span v-tooltipable.themeLight :data-cy="row.name" class="truncate">
            {{ row.value }}
          </span>
          <router-link v-if="row.link" :to="row.link">
            <icon-base
              width="12"
              height="12"
              view-box="0 0 24 24"
              :icon="IconLink"
            />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { ROUTE_DEAL_PROGRESS } from "@/router/routes";

import IconClose from "@/components/icons/IconClose.vue";
import IconCompanySmallNoBg from "@/components/icons/IconCompanySmallNoBg.vue";
import IconLineArrowRight from "@/components/icons/IconLineArrowRight.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconPersons from "@/components/icons/IconPersons.vue";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";

import type { SmsMessageEventData } from "@/models/communicationLogs";

const { sms, isCollapsed, index } = defineProps<{
  sms: SmsMessageEventData;
  index?: number;
  isTopLevel?: boolean;
  isCollapsed?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  open: [id: number, logId: number];
  "collapsed:toggle": [];
}>();

const { t } = useI18n();

const dealLink = computed(() =>
  sms?.linked_instance_id
    ? {
        name: ROUTE_DEAL_PROGRESS,
        params: { id: sms?.linked_instance_id },
      }
    : undefined
);

const infoRows = computed(() => {
  const sections = [
    {
      name: "business-name",
      label: t("COMMON.BUSINESS"),
      iconOptions: {
        icon: IconDeals,
        viewBox: "10 10 20 20",
      },
      value: sms?.application_business_name,
      link: dealLink.value,
    },
    {
      name: "partner-name",
      label: t("COMMON.PARTNER"),
      iconOptions: {
        icon: IconPersons,
        viewBox: "10 10 20 20",
      },
      value: sms?.integration_partner,
    },
    {
      name: "brand-name",
      label: t("COMMON.BRAND"),
      iconOptions: {
        icon: IconCompanySmallNoBg,
        viewBox: "0 0 16 16",
      },
      value: sms?.brand_name,
    },
  ];
  return sections.filter((section) => !!section.value);
});

const cardStyles = computed(() => {
  if (!isCollapsed || !index) {
    return "";
  }
  // scale the cards and stack them to show multiple incoming
  const zIndex = 100 - index;
  const translateY = 10 * index;
  const scale = (20 - index) / 20;
  return {
    zIndex,
    transform: `translateY(${translateY}px) scale(${scale})`,
  };
});

const bgStyle = computed(() => {
  if (!isCollapsed || !index) {
    return {
      backgroundColor: "#272A40",
    };
  }
  return {
    backgroundColor: `${index === 1 ? "#3B3E52" : "#525566"}`,
  };
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";
.sms-session-info-row {
  @apply flex items-center justify-start gap-2 self-stretch;
  .icon {
    @apply flex-none text-label;
  }
}
</style>
